import toast from 'react-hot-toast';
import { getArmorInfo, getWeaponInfo } from '../utils/equipmentHelpers';

/**
 * Custom hook for managing equipment (equipping/unequipping items)
 *
 * @param {Object} character - Current character object
 * @param {Function} updateCharacter - Function to update character in database
 * @param {Object} allData - Game data (equipment database, etc.)
 * @returns {Object} Equipment management functions
 */
const useEquipmentManager = (character, updateCharacter, allData) => {

  // Equipment management function for direct equipping from character sheet
  const equipItem = async (itemId) => {
    const currentEquipment = character.inventory?.equipment || [];
    const currentEquipped = character.equipped || {};

    // Find the item in inventory
    const itemIndex = currentEquipment.findIndex(item => item.id === itemId);
    if (itemIndex === -1) return; // Item not found

    const item = currentEquipment[itemIndex];

    // Determine what type of equipment this is and which slot to use
    let equipmentSlot = {};
    let previouslyEquipped = null;

    // Check if it has weapon-like properties (damage, trait, range)
    if (item.damage && item.trait && item.range) {
      // It's a weapon - determine if primary or secondary based on actual equipment database categorization
      const weaponName = item.name.toLowerCase();
      const isShield = weaponName.includes('shield');

      // Check if this weapon is actually categorized as a secondary weapon in the equipment database
      let isSecondaryWeapon = isShield;

      // Look up in secondary weapons database to determine actual categorization
      if (allData.equipmentDatabase && allData.equipmentDatabase.secondaryWeapons) {
        for (const tierKey in allData.equipmentDatabase.secondaryWeapons) {
          const tier = allData.equipmentDatabase.secondaryWeapons[tierKey];
          for (const weaponKey in tier) {
            const weapon = tier[weaponKey];
            if (weapon && weapon.id === item.id) {
              isSecondaryWeapon = true;
              break;
            }
          }
          if (isSecondaryWeapon) break;
        }
      }

      // Fallback to name-based detection for non-database items (keeping the old logic as backup)
      if (!isSecondaryWeapon && !allData.equipmentDatabase) {
        isSecondaryWeapon = weaponName.includes('small dagger') || weaponName.includes('throwing') ||
                           weaponName.includes('whip') || weaponName.includes('grappler') ||
                           weaponName.includes('hand crossbow');
      }

      if (isSecondaryWeapon) {
        // Check if primary weapon is two-handed
        const currentPrimary = currentEquipped.primaryWeapon || character.equipment?.primaryWeapon;
        if (currentPrimary) {
          const primaryData = getWeaponInfo(currentPrimary, allData, character);
          if (primaryData && primaryData.burden && primaryData.burden.toLowerCase().includes('two')) {
            toast.error('Cannot equip secondary weapon: Primary weapon is two-handed and requires both hands!');
            return;
          }
        }

        // It's a secondary weapon - check both structures for previously equipped
        previouslyEquipped = currentEquipped.secondaryWeapon || character.equipment?.secondaryWeapon;
        equipmentSlot.secondaryWeapon = item.id;
      } else {
        // It's a primary weapon - check both structures for previously equipped
        previouslyEquipped = currentEquipped.primaryWeapon || character.equipment?.primaryWeapon;
        equipmentSlot.primaryWeapon = item.id;

        // Check for two-handed weapons (burden system)
        if (item.burden && item.burden.toLowerCase().includes('two')) {
          // Two-handed weapon unequips secondary weapon
          const currentSecondary = currentEquipped.secondaryWeapon || character.equipment?.secondaryWeapon;
          if (currentSecondary) {
            const secondaryData = getWeaponInfo(currentSecondary, allData, character);
            if (secondaryData) {
              // Add secondary weapon back to inventory
              currentEquipment.push({ ...secondaryData, quantity: 1 });
            }
          }
          equipmentSlot.secondaryWeapon = ''; // Clear secondary slot
        }
      }
    } else if (item.baseScore || item.baseArmorScore || item.name.toLowerCase().includes('armor') ||
               item.name.toLowerCase().includes('clothing') || item.name.toLowerCase().includes('mail')) {
      // It's armor - check both structures for previously equipped
      previouslyEquipped = currentEquipped.armor || character.equipment?.armor;
      equipmentSlot.armor = item.id;
    } else {
      return; // Don't equip if we can't determine the type
    }

    // Remove item from inventory (or decrease quantity)
    let updatedEquipment;
    if (item.quantity > 1) {
      // Decrease quantity
      updatedEquipment = currentEquipment.map((invItem, index) =>
        index === itemIndex ? { ...invItem, quantity: invItem.quantity - 1 } : invItem
      );
    } else {
      // Remove item completely
      updatedEquipment = currentEquipment.filter((_, index) => index !== itemIndex);
    }

    // Add previously equipped item back to inventory (if there was one)
    if (previouslyEquipped && previouslyEquipped !== '') {
      // Find the previously equipped item data
      const previousItem = getWeaponInfo(previouslyEquipped, allData, character) || getArmorInfo(previouslyEquipped, allData, character);
      if (previousItem) {
        // Add it back to inventory
        updatedEquipment.push({ ...previousItem, quantity: 1 });
      }
    }

    const updatedCharacter = {
      ...character,
      // Update the equipped structure (primary)
      equipped: {
        ...currentEquipped,
        ...equipmentSlot
      },
      // Also update the equipment structure for backward compatibility
      equipment: {
        ...character.equipment,
        ...equipmentSlot
      },
      inventory: {
        ...character.inventory,
        equipment: updatedEquipment
      }
    };

    await updateCharacter(updatedCharacter);
  };

  // Function to unequip an item and return it to inventory
  const unequipItem = async (slot) => {
    const currentEquipped = character.equipped || {};
    const itemId = currentEquipped[slot];

    if (!itemId) return; // Nothing to unequip

    // Get item data to add back to inventory
    const itemData = slot === 'armor' ? getArmorInfo(itemId, allData, character) : getWeaponInfo(itemId, allData, character);

    if (itemData) {
      // Add item back to inventory
      const currentEquipment = character.inventory?.equipment || [];
      const updatedEquipment = [...currentEquipment, { ...itemData, quantity: 1 }];

      // Remove from equipped slots
      const updatedEquipped = {
        ...currentEquipped,
        [slot]: '' // Clear the slot
      };

      // Also update the equipment structure for backward compatibility
      const updatedEquipmentStruct = {
        ...character.equipment,
        [slot]: ''
      };

      // Update character
      const updatedCharacter = {
        ...character,
        equipped: updatedEquipped,
        equipment: updatedEquipmentStruct,
        inventory: {
          ...character.inventory,
          equipment: updatedEquipment
        }
      };

      await updateCharacter(updatedCharacter);
    }
  };

  return {
    equipItem,
    unequipItem
  };
};

export default useEquipmentManager;
